import React, { useState, useEffect } from 'react';
import ModelSelect, { DEFAULT_MODELS, MODELS } from './ModelSelect';
import TokenInput from './TokenInput';
import ToggleSwitch from './ToggleSwitch';
import { validateToken, deleteAIKey } from 'config/axiosAIKeys';
import { useTranslation } from 'react-i18next';
import ExternalLinkIcon from 'icons/ExternalLinkIcon';

const PROVIDER_META = {
    openai: {
        logo: '/Images/ai-providers/openai.png',
        apiLink: 'https://platform.openai.com/api-keys',
    },
    claude: {
        logo: '/Images/ai-providers/claude.png',
        apiLink: 'https://platform.claude.com/settings/keys',
    },
    gemini: {
        logo: '/Images/Google-Gemini-Logo.png',
        apiLink: 'https://aistudio.google.com/apikey',
    },
    perplexity: {
        logo: '/Images/ai-providers/perplexity.png',
        apiLink: 'https://docs.perplexity.ai/docs/admin/api-key-management',
    },
};

export default function AIProviderCard({ provider, title, initialData, onChange, onDelete }) {
    const { t } = useTranslation();
    const meta = PROVIDER_META[provider];

    const [token, setToken] = useState('');
    const [keyChanged, setKeyChanged] = useState(false);
    const [id, setId] = useState(null);

    const [showToken, setShowToken] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [model, setModel] = useState(null);
    const [status, setStatus] = useState(null);
    const [hasExisting, setHasExisting] = useState(false);

    useEffect(() => {
        if (initialData) {
            setEnabled(initialData.enabled ?? false);
            setId(initialData.id ?? null);
            setHasExisting(initialData.hasExisting ?? false);

            const list = MODELS[provider] || [];
            const found = initialData.model
                ? list.find((m) => m.type === initialData.model.type)
                : null;
            setModel(found || initialData.model || DEFAULT_MODELS[provider]);
            setToken(initialData.token || '');
            setKeyChanged(false);
        } else {
            setModel(DEFAULT_MODELS[provider]);
            setHasExisting(false);
            setToken('');
            setKeyChanged(false);
        }
    }, [initialData, provider]);


    useEffect(() => {
        onChange?.({
            id,
            name: provider,
            enabled,
            apiKey: token,
            keyChanged,
            hasExisting,
            model: model?.type,
        });
    }, [id, provider, enabled, token, keyChanged, model, hasExisting, onChange]);

    const handleTokenChange = (val) => {
        setToken(val);
        setKeyChanged(val.length > 0);
        if (val.length === 0 && !hasExisting) {
            setKeyChanged(false);
        }
    };

    const showStatus = (type, message) => setStatus({ type, message });

    const handleTest = async () => {
        if (!token && !hasExisting) {
            showStatus('invalid', t('Veuillez saisir une clé API avant de tester.'));
            return;
        }
        showStatus('testing', t('Test en cours…'));
        try {
            const result = await validateToken(provider, token, model?.type);
            showStatus(
                result?.valid ? 'valid' : 'invalid',
                result?.valid
                    ? t('Connexion validée ✓')
                    : result?.message || t('Clé invalide ou connexion échouée.')
            );
        } catch {
            showStatus('invalid', t('Erreur lors du test de connexion.'));
        }
    };

    const handleDelete = async () => {
        const wasExisting = hasExisting;
        setToken('');
        setKeyChanged(false);
        setStatus(null);
        setHasExisting(false);
        setEnabled(false);
        setModel(DEFAULT_MODELS[provider]);
        setId(null);
        onDelete?.(provider);

        if (!wasExisting) {
            showStatus('valid', t('Clé supprimée avec succès.'));
            return;
        }

        try {
            await deleteAIKey(provider);
            showStatus('valid', t('Clé supprimée avec succès.'));
        } catch (err) {
            console.warn('API delete failed, but local deletion succeeded:', err);
            showStatus('valid', t('Clé supprimée localement.'));
        }
    };
    const handleToggle = (newVal) => {
        setEnabled(newVal);
    };

    const statusClass = status
        ? ({
            testing: 'ai-status-msg testing',
            valid: 'ai-status-msg valid',
            invalid: 'ai-status-msg invalid',
        }[status.type] ?? 'ai-status-msg')
        : '';

    return (
        <div className={`ai-card ${enabled ? 'ai-card-enabled' : ''}`}>
            <div className="ai-card-header">
                <div className="ai-card-header-left">
                    <div className="ai-provider-avatar">
                        <img src={meta.logo} alt={title} />
                    </div>
                    <div className="ai-provider-info">
                        <div className="ai-provider-title-row">
                            <strong className="ai-provider-name">{title}</strong>
                            <span
                                className={`ai-connection-dot ${enabled ? 'dot-connected' : 'dot-inactive'}`}
                            />
                            <span
                                className={`ai-connection-label ${enabled ? 'label-connected' : 'label-inactive'}`}
                            >
                                {enabled ? t('Connecté') : t('Non configuré')}
                            </span>
                        </div>
                    </div>
                </div>

                <ToggleSwitch enabled={enabled} setEnabled={handleToggle} />
            </div>

            <div className="ai-card-body">
                <ModelSelect provider={provider} value={model} onChange={setModel} />

                <TokenInput
                    token={token}
                    setToken={handleTokenChange}
                    showToken={showToken}
                    setShowToken={setShowToken}
                    placeholder={
                        hasExisting && !keyChanged
                            ? '••••••••••••••••'
                            : t('Saisir votre clé API…')
                    }
                />

                <div className="ai-card-footer">
                    <a
                        className="ai-api-link"
                        href={meta.apiLink}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <ExternalLinkIcon size={13} color="#6458F1" />
                        {t('Obtenir une clé API')}
                    </a>

                    <div className="ai-actions">
                        <button
                            className="ai-btn-ghost"
                            onClick={() => setShowToken(!showToken)}
                        >
                            {showToken ? t('Masquer') : t('Afficher')}
                        </button>
                        <button
                            className="ai-btn-ghost"
                            onClick={handleTest}
                            disabled={status?.type === 'testing'}
                        >
                            {t('Tester')}
                        </button>
                        <button className="ai-btn-ghost" onClick={handleDelete}>
                            {t('Supprimer')}
                        </button>
                    </div>
                </div>

                {status && <div className={statusClass}>{status.message}</div>}
            </div>
        </div>
    );
}